// ═══════════════════════════════════════════════════════════════════
// vyenModel3D.jsx — V'YEN, SCULPTED   (caP'n, 2026-09-16; REBUILT 2026-09-23)
// ═══════════════════════════════════════════════════════════════════
// Sweet Treats' keeper — the big lamia at her booth. caP'n, 2026-09-23:
// "sculpt rig and add V'yren into Terror proper the way we did V'ael",
// "definitely a big lady", "definitely wrap around the booth".
//
// WHAT IT IS. `public/models/vyen.glb` — REBUILT IN BLENDER 2026-09-23 on
// V'ael's pipeline (master gamemaster/models/sculpt/glb/vyen.glb, builder
// gamemaster/models/sculpt/builders/vyen_blender/), off caP'n's two
// pictures of her (full body; at the kiosk). The 2026-09-16 SDF body is
// kept beside it as vyen_v1_sdf.glb.
//   · WORLD METRES, not a unit body: her serpent coil (24.7 m of it) is
//     built round the real booth (plaza_stall.glb, whose origin is 0.40 m
//     in front of her), so the file must be drawn at scale 1. MODEL_TOP is
//     her CROWN (the coil's arches rise higher, to 2.72 m) and VYEN_H in
//     plazaFolk3D.jsx is set to it: h / MODEL_TOP = 1. Her trunk is at the
//     origin, the floor at y = 0, she faces +z — and her FACING MUST BE
//     {x:0, y:1} (Plaza3D) or the coil turns off the booth.
//   · 18 named meshes on ONE 35-bone skeleton: V'ael's upper body (spine,
//     chest, neck, head, arms), the serpent's base/trunk, one bone per coil
//     arch, three for the tail tip, and two per head-snake (root, mid-body).
//   · The SKIN and the head-SNAKES carry vertex colours × their textures;
//     the coil's scales are REPEATING textures.
//   · ⚑ HER ARMS ARE SCULPTED RESTING ON THE COUNTER (left hand on the open
//     ledger, right hand on the wood). This file adds the lean and the life,
//     and PINS THE HANDS: after the body is posed, a two-bone IK puts each
//     elbow between the shoulder (wherever the body took it) and the hand
//     (where the lean-only pose holds it) — the shoulders breathe and shift,
//     the hands never slide on the wood.
//   · DRAWN AT SCALE 1, whatever `h` says (h only sizes the painted stand-in).
// Life: breathing, a slow sway, she looks round the Plaza; every ~8 s a
// MOMENT — she looks up at you, a head-snake rears, or her shoulders
// shift; finger-taps in bursts; the snakes' bends travel from root to
// head; the arches rock; the tail tip sweeps; and a slow SWELL rolls down
// the coil (a vertex wave keyed on the coil's own UV, below). When you
// come to the counter she turns to you and lifts her chin.
//
// Same contract and safety net as the other folk sculpts:
// <VyenSculpted ctl h Fallback> for r3f scenes; the painted VyenBody
// stands in while the file loads or if it fails, and failSafe() in
// plazaFolk3D.jsx swaps it in for good if the frame rate can't hold.
// ctl: {facing, speed, player?} — player is the Plaza's posRef (tiles).
// PERF LAW: nothing here sets React state from a frame.
// ⚠ vyenPose / h01 / beat / taps are MIRRORED in the builder (vy_rig.py,
//   vyen_pose()); its pose table (vy_export.pose_table) is the parity test.
// ═══════════════════════════════════════════════════════════════════
import { Component, Suspense, useMemo, useRef } from "react";
import { useFrame, useLoader } from "@react-three/fiber";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { clone as cloneSkinned } from "three/examples/jsm/utils/SkeletonUtils.js";

export const VYEN_GLB_URL = `${process.env.PUBLIC_URL || ""}/models/vyen.glb`;
export const MODEL_TOP = 2.2417;     // measured at export (Blender build, 2026-09-23; face pass: 2.2446 → 2.2417, the crown snakes moved to the temples): her crown — NOT the arches (2.716)
// THE INK HULL'S WIDTH, PER PART — mirrored in the builder's vy_look.py (INK_BY_PART). 0 = no line.
const INK_W = 0.0032;
// (the jewellery's 1–1.4 mm lines were under a pixel at every game camera and cost ~50k triangles: gone)
const INK_BY_PART = [[/snake_eyes/, 0], [/inlay/, 0], [/pearls|beads|gems|gold/, 0], [/snakes/, 0.0022]];
const inkWidth = (name) => (INK_BY_PART.find(([re]) => re.test(name)) || [null, INK_W])[1];
const SELF_LIGHT = 0.4;
const COIL_LIGHT = 0.15;             // the serpent's own light: at 0.4 it flattened to periwinkle; the art is charcoal-indigo
const GEMS = /gems/;                 // the red stones hold a little light of their own
const COIL = /vyen_serpent/;         // the swell rolls down the coil's body (the trunk, the bands stay put)
const SCALES = /vyen_serpent|vyen_trunk/;
// her whole extent (model space, measured at export): x −4.59…2.59, y 0…2.72, z −1.14…2.55 — one sphere
// for every mesh, so the renderer can cull her (and skip her skinning) when she is off screen
const BOUND = new THREE.Sphere(new THREE.Vector3(-1.0, 1.36, 0.71), 4.5);

let RAMP = null;
const toonRamp = () => {
  if (RAMP) return RAMP;
  RAMP = new THREE.DataTexture(new Uint8Array([172, 172, 172, 255, 228, 228, 228, 255, 255, 255, 255, 255]), 3, 1);
  RAMP.minFilter = RAMP.magFilter = THREE.NearestFilter;
  RAMP.needsUpdate = true;
  return RAMP;
};

// ── THE SWELL ──────────────────────────────────────────────────────
// A hump that rolls from her trunk toward the tail, keyed on the coil's own
// texture coordinate v (the builder sweeps it as ∫ds / 2πr: 1.81 where the
// body leaves the trunk, 24.2 at the tip). Zero on the belly (it lies on the
// floor), zero under the five gold bands (their rims sit only ~2 cm proud),
// gone before the tail tip (its own bones move it). Displaced along the
// bind-pose normal BEFORE skinning; the outline hull does the same.
const SWELL_T = { value: 0 };
const BAND_V = [2.9885, 5.8763, 8.2328, 13.9751, 16.1319];   // the bands' centres in v (builder: vy_serpent.BANDS)
const SWELL_AMP = 0.06;             // metres at the crest (integration critique #4: 2.5 cm didn't read)
const SWELL_GLSL = `
uniform float uSwellT;
varying float vSwell;
float vyenSwell(vec2 q, vec3 n) {
  float v = q.y;
  float m = smoothstep(1.81, 2.5, v) * (1.0 - smoothstep(17.4, 18.3, v));
  float bd = min(min(min(abs(v - ${BAND_V[0]}), abs(v - ${BAND_V[1]})), min(abs(v - ${BAND_V[2]}), abs(v - ${BAND_V[3]}))), abs(v - ${BAND_V[4]}));
  m *= smoothstep(0.08, 0.18, bd);
  float w = 0.5 + 0.5 * sin(v * 2.856 - uSwellT * 0.9);
  return w * w * w * w * m;
}
`;
const addSwell = (sh, sheen) => {
  sh.uniforms.uSwellT = SWELL_T;
  sh.vertexShader = SWELL_GLSL + sh.vertexShader.replace("#include <begin_vertex>",
    "#include <begin_vertex>\n  vSwell = vyenSwell( uv, normal );\n  transformed += normal * (" + SWELL_AMP.toFixed(3) + " * vSwell * smoothstep( -0.2, 0.5, normal.y ));");
  // the crest catches a little light as it passes: a travelling sheen
  if (sheen) sh.fragmentShader = "varying float vSwell;\n" + sh.fragmentShader.replace("#include <emissivemap_fragment>",
    "#include <emissivemap_fragment>\n  totalEmissiveRadiance += vec3( 0.022, 0.026, 0.05 ) * vSwell * vSwell;");
};

const INK = new Map();
const inkMat = (w, swell) => {
  const key = w + (swell ? "s" : "");
  let m = INK.get(key);
  if (m) return m;
  m = new THREE.MeshBasicMaterial({ color: 0x161114, side: THREE.BackSide });
  m.onBeforeCompile = (sh) => {
    if (swell) addSwell(sh, false);
    sh.vertexShader = sh.vertexShader.replace("#include <begin_vertex>",
      "#include <begin_vertex>\n  transformed += normalize( normal ) * " + w.toFixed(5) + ";");
  };
  m.customProgramCacheKey = () => "vyenInk" + key;      // two widths share a signature without this
  INK.set(key, m);
  return m;
};

function prepare(scene) {
  const root = cloneSkinned(scene);
  const meshes = [];
  root.traverse((o) => { if (o.isMesh) meshes.push(o); });
  meshes.forEach((o) => {
    const m = o.material;
    if (m.map) m.map.colorSpace = THREE.SRGBColorSpace;
    const vc = !!o.geometry.attributes.color;
    const swell = COIL.test(o.name);
    o.material = new THREE.MeshToonMaterial({
      color: m.color, map: m.map, gradientMap: toonRamp(), side: m.side, vertexColors: vc,
      emissive: m.color.clone().multiplyScalar(SCALES.test(o.name) ? COIL_LIGHT : SELF_LIGHT), emissiveMap: m.map || null,
    });
    // vertex-coloured parts keep their colour in the vertices: the self-light
    // has to be scaled by it too, or it washes them out to grey
    if (vc || swell) {
      o.material.onBeforeCompile = (sh) => {
        if (vc) sh.fragmentShader = sh.fragmentShader.replace("vec3 totalEmissiveRadiance = emissive;",
          "vec3 totalEmissiveRadiance = emissive * vColor.rgb;");
        if (swell) addSwell(sh, true);
      };
      o.material.customProgramCacheKey = () => "vyen" + (vc ? "VC" : "") + (swell ? "Swell" : "");
    }
    o.boundingSphere = BOUND; o.frustumCulled = true;      // (a SkinnedMesh culls by its own sphere; set, it is never recomputed)
    if (GEMS.test(o.name)) o.material.emissive = new THREE.Color(0x7a0818);
    if (/hair/.test(o.name)) o.material.emissive.multiplyScalar(0.35);   // silver, not a lamp, under the Plaza's violet light
    const w = inkWidth(o.name);
    if (w > 0) {
      const ink = o.isSkinnedMesh ? new THREE.SkinnedMesh(o.geometry, inkMat(w, swell)) : new THREE.Mesh(o.geometry, inkMat(w, swell));
      if (o.isSkinnedMesh) ink.bind(o.skeleton, o.bindMatrix);
      ink.name = o.name + "_ink"; ink.boundingSphere = BOUND; ink.frustumCulled = true;
      o.parent.add(ink);
    }
  });
  const B = {};
  root.traverse((o) => { if (o.isBone) B[o.name] = o; });
  // THE PIN'S REFERENCE: the LEAN-ONLY pose — where the sculpt's hands sit on the counter. Per arm, in
  // model space: the shoulder, elbow and hand joints, and each bone's world rotation.
  Object.values(B).forEach((b) => b.rotation.set(0, 0, 0));
  if (B.spine) B.spine.rotation.x = LEAN[0];
  if (B.chest) B.chest.rotation.x = LEAN[1];
  root.updateMatrixWorld(true);
  const inv = new THREE.Matrix4().copy(root.matrixWorld).invert();
  const PIN = {};
  for (const k of ["L", "R"]) {
    const u = B["upperarm" + k], f = B["forearm" + k], h = B["hand" + k];
    if (!u || !f || !h) continue;
    const ref = (b) => { const M = inv.clone().multiply(b.matrixWorld); const p = new THREE.Vector3(), q = new THREE.Quaternion(), s = new THREE.Vector3(); M.decompose(p, q, s); return { p, q }; };
    const U = ref(u), F = ref(f), H = ref(h);
    PIN[k] = { S0: U.p, E0: F.p, H0: H.p, Qu: U.q, Qf: F.q, Qh: H.q,
      a: F.p.distanceTo(U.p), b: H.p.distanceTo(F.p) };
  }
  return { root, B, PIN };
}

// ── THE POSE (⚠ mirrored in vy_rig.vyen_pose) ──────────────────────
const LEAN = [0.11, 0.09];                                            // spine, chest (forward)
const ARCH_AXIS = { archW: [0.603, -0.797], archE: [0.207, 0.978] };  // (x, z) of the line between each arch's feet
const SNAKE_PH = [0.0, 2.1, 4.0, 1.3, 3.3, 5.1, 0.7, 2.8];
const SNAKE_F = [0.55, 0.62, 0.71, 0.58, 0.83, 0.66, 0.77, 0.52];
const SNAKE_LIFT = [[-0.7457, 0.0, 0.6662], [-0.7096, 0.0, -0.7046], [-0.1157, 0.0, 0.9933], [-0.1277, 0.0, -0.9918],
  [-0.7024, 0.0, 0.7118], [-0.095, 0.0, -0.9955], [-0.5269, 0.0, 0.8499], [-0.088, 0.0, -0.9961]];   // love pass 2026-09-23: re-read (the snakes now rear)
// a repeatable 0..1 hash (the builder's _h)
export const h01 = (x) => { const v = Math.sin(x * 12.9898 + 78.233) * 43758.5453; return v - Math.floor(v); };
const BEAT_SLOT = 8.0, BEAT_LEN = 2.6;
// the MOMENT running at t: [kind, which, p] or null — 0 she looks up at you, 1 a head-snake rears, 2 her shoulders shift
export function beat(t) {
  const n = Math.floor(t / BEAT_SLOT);
  const tau = t - n * BEAT_SLOT - (0.6 + (BEAT_SLOT - BEAT_LEN - 1.2) * h01(n));
  if (tau < 0 || tau > BEAT_LEN) return null;
  const kh = h01(n + 0.37);
  return [kh < 0.45 ? 0 : kh < 0.75 ? 1 : 2, Math.floor(h01(n + 0.71) * 8), tau / BEAT_LEN];
}
const bump = (p) => Math.sin(Math.PI * p) ** 2;
const dart = (p) => (p < 0.16 ? p / 0.16 : (1 - (p - 0.16) / 0.84) ** 2);    // fast up, slow settle
// bursts of three finger-taps, now and then: [amount, hand +1 left / −1 right]
export function taps(t) {
  const n = Math.floor(t / 5.0);
  if (h01(n + 9.1) > 0.55) return [0, 1];
  const tau = t - n * 5.0 - (0.5 + 3.2 * h01(n + 9.4));
  if (tau < 0 || tau > 0.9) return [0, 1];
  return [Math.max(0, Math.sin(2 * Math.PI * 3.3 * tau)) ** 2, h01(n + 9.7) < 0.7 ? 1 : -1];
}

// {bone: [rx, ry, rz]} — talk 0..1 (a customer at the counter), look = angle to the player (+ = her left),
// attend 0..1 = someone near enough and in front (the "looks up at you" moment is only for them)
export function vyenPose(t, talk = 0, look = 0, attend = 1) {
  const P = {};
  const sw = Math.sin(t * 0.55);
  const b = beat(t);
  const [kind, which, p] = b || [-1, 0, 0];
  const shift = kind === 2 ? bump(p) * (h01(Math.floor(t / BEAT_SLOT) + 0.2) < 0.5 ? 1 : -1) : 0;
  P.base = [Math.sin(t * 0.40) * 0.010, 0, sw * 0.016];
  P.trunk = [Math.sin(t * 0.40 + 0.8) * 0.010, Math.sin(t * 0.30) * 0.025, -sw * 0.010];
  P.hips = [0, Math.sin(t * 0.30 + 0.5) * 0.025 + shift * 0.05, -sw * 0.006];
  const br = Math.sin(t * 1.3);
  P.spine = [LEAN[0] + br * 0.006, 0, -shift * 0.03];
  P.chest = [LEAN[1] + br * 0.008, -Math.sin(t * 0.30) * 0.025, shift * 0.08];
  const lk = Math.max(-0.7, Math.min(0.7, look));
  const wander = Math.sin(t * 0.33) * 0.16;
  const atYou = kind === 0 ? bump(p) * attend : 0;
  const yaw = wander * (1 - talk) + lk * Math.max(talk, atYou);
  const up = 0.15 * Math.max(talk, atYou);
  P.neck = [-0.09 - up * 0.4, yaw * 0.35, -shift * 0.04];
  P.head = [-0.06 - up * 0.6 + Math.sin(t * 0.7) * 0.015, yaw * 0.65, 0.05 + Math.sin(t * 0.45) * 0.035 - shift * 0.05];
  const [tap, hand] = taps(t);
  for (const [k, s] of [["L", 1], ["R", -1]]) {        // the counter pose is SCULPTED: only deltas here
    P["upperarm" + k] = [0, 0, 0];
    P["forearm" + k] = [0, 0, 0];
    P["hand" + k] = [s === hand ? -0.09 * tap : 0, 0, 0];
  }
  for (const [n, ph] of [["archW", 0.0], ["archE", 1.9]]) {
    const [ax, az] = ARCH_AXIS[n]; const a = Math.sin(t * 0.31 + ph) * 0.035;
    P[n] = [ax * a, 0, az * a];
  }
  P.tip1 = [0, Math.sin(t * 0.6) * 0.10, 0];
  P.tip2 = [0, Math.sin(t * 0.6 - 0.6) * 0.16, 0];
  P.tip3 = [0, Math.sin(t * 0.6 - 1.2) * 0.22 + Math.sin(t * 1.7) * 0.05, 0];
  const calm = 1 - 0.5 * talk;
  for (let i = 0; i < 8; i++) {
    const n = "snake" + (i + 1);
    const ph = SNAKE_PH[i], f = SNAKE_F[i], crown = i < 2;
    const a = (crown ? 0.035 : 0.08) * calm;
    const [lx, ly, lz] = SNAKE_LIFT[i];
    const w1 = Math.sin(t * f + ph), w2 = Math.sin(t * f + ph - 1.3);   // the bend TRAVELS root → mid
    const rear = kind === 1 && which === i ? dart(p) : 0;
    const liftR = (crown ? 0 : a * 0.8 * w1) + 0.50 * rear;           // the whole snake stands up off her head…
    const liftM = a * 1.4 * w2 + 0.12 * rear;                          // …its head only a touch (it is already curled up)
    const yawR = a * w1 * (crown ? 0.6 : 1.0), yawM = a * 1.3 * w2;
    P[n] = [lx * liftR, ly * liftR + yawR, lz * liftR];
    P[n + "b"] = [lx * liftM, ly * liftM + yawM, lz * liftM];
  }
  return P;
}

// ── THE PIN: the hands stay on the counter, the elbows take up the body's motion ──
// (⚠ mirrored in vy_export.arm_ik). Everything in MODEL space (relative to her root).
const _I = new THREE.Matrix4(), _M = new THREE.Matrix4(), _W = new THREE.Vector3();
const _S = new THREE.Vector3(), _u = new THREE.Vector3(), _u0 = new THREE.Vector3(), _p = new THREE.Vector3();
const _E = new THREE.Vector3(), _H = new THREE.Vector3(), _a = new THREE.Vector3(), _b = new THREE.Vector3();
const _q = new THREE.Quaternion(), _qu = new THREE.Quaternion(), _qf = new THREE.Quaternion(), _qh = new THREE.Quaternion();
const _qc = new THREE.Quaternion(), _qt = new THREE.Quaternion(), _e = new THREE.Euler();
const _pos = new THREE.Vector3(), _scl = new THREE.Vector3();
function pinArm(B, R, k, handDelta) {
  const u = B["upperarm" + k], f = B["forearm" + k], h = B["hand" + k];
  // the shoulder, where the body took it (model space) and the chest's rotation
  _M.copy(_I).multiply(u.matrixWorld); _M.decompose(_S, _qt, _scl);
  _M.copy(_I).multiply(u.parent.matrixWorld); _M.decompose(_pos, _qc, _scl);
  // the elbow, in the sculpt's bending plane
  _u.subVectors(R.H0, _S); let d = _u.length(); _u.divideScalar(d);
  d = Math.min(Math.max(d, Math.abs(R.a - R.b) + 1e-6), R.a + R.b - 1e-6);
  _u0.subVectors(R.H0, R.S0).normalize();
  _p.subVectors(R.E0, R.S0); _p.addScaledVector(_u0, -_p.dot(_u0));
  _p.addScaledVector(_u, -_p.dot(_u)).normalize();
  const x = (R.a * R.a - R.b * R.b + d * d) / (2 * d), hh = Math.sqrt(Math.max(R.a * R.a - x * x, 0));
  _E.copy(_S).addScaledVector(_u, x).addScaledVector(_p, hh);
  _H.copy(_S).addScaledVector(_u, d);
  // world rotations: the sculpt's, turned by the least rotation onto the new bones
  _qu.setFromUnitVectors(_a.subVectors(R.E0, R.S0).normalize(), _b.subVectors(_E, _S).normalize()).multiply(R.Qu);
  _qf.setFromUnitVectors(_a.subVectors(R.H0, R.E0).normalize(), _b.subVectors(_H, _E).normalize()).multiply(R.Qf);
  _qh.copy(R.Qh).multiply(_qt.setFromEuler(_e.set(handDelta[0], handDelta[1], handDelta[2])));
  // …as local rotations down the chain (rest rotations are identity; translations untouched)
  u.quaternion.copy(_qc).invert().multiply(_qu);
  f.quaternion.copy(_qu).invert().multiply(_qf);
  h.quaternion.copy(_qf).invert().multiply(_qh);
}
function applyPose(root, B, PIN, P) {
  for (const n in P) { const b = B[n]; if (b) { const r = P[n]; b.rotation.set(r[0], r[1], r[2]); } }
  root.updateMatrixWorld(true);
  _I.copy(root.matrixWorld).invert();
  for (const k of ["L", "R"]) if (PIN[k]) pinArm(B, PIN[k], k, P["hand" + k] || [0, 0, 0]);
}

export function VyenModel({ ctl }) {
  const gltf = useLoader(GLTFLoader, VYEN_GLB_URL);
  const { root, B, PIN } = useMemo(() => prepare(gltf.scene), [gltf]);
  const g = useRef(); const yaw = useRef(0); const talk = useRef(0); const look = useRef(0); const attend = useRef(0);
  useFrame(({ clock }, dtRaw) => {
    const o = g.current; if (!o) return;
    const dt = Math.min(dtRaw || 0.016, 0.05); const t = clock.getElapsedTime() + 2.1;
    SWELL_T.value = t;
    const f = (ctl && ctl.facing) || { x: 0, y: 1 };
    const want = Math.atan2(f.x || 0, f.y === undefined ? 1 : f.y);
    let d = want - yaw.current; while (d > Math.PI) d -= Math.PI * 2; while (d < -Math.PI) d += Math.PI * 2;
    yaw.current += d * Math.min(1, dt * 5);   // a big body turns slowly
    o.rotation.y = yaw.current;
    // the customer: in FRONT of her and near, she turns to them (Plaza tiles: a tile's centre is +0.5).
    // Behind her or far off, she keeps to herself — no staring at the wall, no snap as they cross behind.
    const pr = ctl && ctl.player && ctl.player.current;
    let wTalk = 0, wLook = 0, wAttend = 0;
    if (pr) {
      o.getWorldPosition(_W);
      const dx = pr.x + 0.5 - _W.x, dz = pr.y + 0.5 - _W.z;
      const dist = Math.hypot(dx, dz);
      let a = Math.atan2(dx, dz) - yaw.current; while (a > Math.PI) a -= Math.PI * 2; while (a < -Math.PI) a += Math.PI * 2;
      const front = Math.max(0, Math.min(1, (1.4 - Math.abs(a)) / 0.4));       // 1 within ±1.0 rad, 0 past ±1.4
      wTalk = (dist < 3.6 ? 1 : dist > 5.2 ? 0 : (5.2 - dist) / 1.6) * front;
      wAttend = (dist < 9 ? 1 : dist > 12 ? 0 : (12 - dist) / 3) * front;
      wLook = front > 0 ? Math.max(-1.0, Math.min(1.0, a)) : 0;
    }
    talk.current += (wTalk - talk.current) * Math.min(1, dt * 2);
    attend.current += (wAttend - attend.current) * Math.min(1, dt * 2);
    look.current += (wLook - look.current) * Math.min(1, dt * 3);
    applyPose(root, B, PIN, vyenPose(t, talk.current, look.current, attend.current));
  });
  return (
    <group ref={g}>
      <primitive object={root} />
    </group>
  );
}

class GlbBoundary extends Component {
  constructor(p) { super(p); this.state = { bad: false }; }
  static getDerivedStateFromError() { return { bad: true }; }
  componentDidCatch(e) { console.warn("[vyen] vyen.glb did not load — the painted body stands in", e); }
  render() { return this.state.bad ? this.props.fallback : this.props.children; }
}

export function VyenSculpted({ ctl, h, Fallback }) {
  const fb = Fallback ? <Fallback ctl={ctl} h={h} /> : null;
  return (
    <GlbBoundary fallback={fb}>
      <Suspense fallback={fb}>
        <VyenModel ctl={ctl} h={h} />
      </Suspense>
    </GlbBoundary>
  );
}

export default VyenModel;
